"use strict";

const moment = require("moment");

/**
 * MOB-058 — wall-clock time at the API boundary.
 *
 * A booking time is the time on the clock of the PointSell. A lesson at 08:00 starts at 08:00
 * there, wherever the customer is. The backend stores and compares these values with no zone
 * attached. So the offset of a value carries no information.
 *
 * `toWire` is the 1 writer. It gives `YYYY-MM-DDTHH:mm:ssZ`, the digits with a constant `Z` and
 * no milliseconds. The `Z` is a stamp that the AppSync scalar `AWSDateTime` requires. It does not
 * say that the value is UTC. `read` and `sortKey` are the readers. They take the digits of every
 * old form (`+02:00`, `-04:00`, `+00:00`, `Z`, no offset) and ignore the offset. `text` prints
 * the digits and `compare` sorts by them. The e-mail of the layer and the pages of the
 * storefront use both, so the 2 runtimes print and sort a booking time the same way.
 *
 * A string is read by its digits. It never goes through `moment(v)`, `moment.utc(v)`,
 * `moment.parseZone(v)`, `Date.parse(v)`, `new Date(v)` or `.tz(...)`. Each of them can move the
 * digits. Even `moment.parseZone` reads a string with no offset as local time first, so a time
 * in a daylight-saving gap of the runtime zone moves by 1 hour.
 *
 * The Lambdas require this file from the layer. The storefront imports it through the adapter
 * `src/libs/WallClock.js` (`layerCjsToEsm` in `vite.config.js`) — see ADR 0001. Keep the
 * `const X = require(...)` and `module.exports = X` shape, and keep to syntax that `nodejs22.x`
 * runs.
 *
 * The instants are outside this module: `Orderr.expireAt`, and `createdAt` and `updatedAt` of
 * every `@model`, are real UTC times.
 */

// The digits of a wall-clock time: a date, then an optional time with optional seconds, an
// optional fraction and an optional offset (`Z`, `+02`, `+0200` or `+02:00`). A space instead of
// the `T` is the text form of Postgres.
const DIGITS =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(Z|[+-]\d{2}(?::?\d{2})?)?)?$/;

function isDate(value) {
  return Object.prototype.toString.call(value) === "[object Date]";
}

/**
 * Minutes east of UTC for an offset text of the `DIGITS` pattern. No offset is 0, as
 * `moment.parseZone` gives it.
 */
function offsetMinutes(text) {
  if (text == null || text === "Z") {
    return 0;
  }
  const sign = text[0] === "-" ? -1 : 1;
  const digits = text.slice(1).replace(":", "");
  const hours = Number(digits.slice(0, 2));
  const minutes = digits.length > 2 ? Number(digits.slice(2, 4)) : 0;
  return sign * (hours * 60 + minutes);
}

/**
 * The digits of a string, as a UTC moment that shows them, and the offset of the string. Null
 * when the string does not have the pattern or names a date that does not exist.
 */
function fromString(text) {
  const match = DIGITS.exec(text);
  if (match == null) {
    return null;
  }
  // The fraction is a decimal: `.5` is 500 ms, and digits after the third are dropped.
  const fraction = match[7] != null ? Number((match[7] + "00").slice(0, 3)) : 0;
  const digits = moment.utc([
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    match[4] != null ? Number(match[4]) : 0,
    match[5] != null ? Number(match[5]) : 0,
    match[6] != null ? Number(match[6]) : 0,
    fraction,
  ]);
  if (!digits.isValid()) {
    return null;
  }
  return { digits: digits, offset: offsetMinutes(match[8]) };
}

/**
 * The digits of a value in any accepted form, and its offset in minutes. Null when the value is
 * null or unreadable.
 *
 * - A string gives its digits.
 * - A moment gives the digits that it shows in its own offset.
 * - A `Date` gives its local fields.
 */
function parse(value) {
  if (value == null) {
    return null;
  }
  if (typeof value === "string") {
    return fromString(value.trim());
  }
  if (moment.isMoment(value)) {
    if (!value.isValid()) {
      return null;
    }
    return {
      digits: moment.utc([
        value.year(),
        value.month(),
        value.date(),
        value.hours(),
        value.minutes(),
        value.seconds(),
        value.milliseconds(),
      ]),
      offset: value.utcOffset(),
    };
  }
  if (isDate(value)) {
    if (isNaN(value.getTime())) {
      return null;
    }
    return {
      digits: moment.utc([
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        value.getHours(),
        value.getMinutes(),
        value.getSeconds(),
        value.getMilliseconds(),
      ]),
      offset: -value.getTimezoneOffset(),
    };
  }
  return null;
}

// The value for the message of an Error. `String` throws for an object with no prototype.
function quote(value) {
  if (typeof value === "string") {
    return JSON.stringify(value);
  }
  try {
    return String(value);
  } catch {
    return Object.prototype.toString.call(value);
  }
}

class WallClock {
  /**
   * The 1 writer: `YYYY-MM-DDTHH:mm:ssZ`. The milliseconds go. `null` and `undefined` give
   * `null`. An unreadable value throws, so the API never gets the text `Invalid date`.
   */
  static toWire(value) {
    if (value == null) {
      return null;
    }
    const parsed = parse(value);
    if (parsed == null) {
      throw new Error("WallClock.toWire: not a wall-clock time: " + quote(value));
    }
    // The digits are a UTC moment, so its ISO text shows them. The native ISO text uses no
    // locale, and the slice drops the milliseconds.
    return parsed.digits.toISOString().slice(0, 19) + "Z";
  }

  /**
   * The 1 reader: a new moment that shows the digits of the value, with the semantics of
   * `moment.parseZone`. The moment keeps the offset of the value, so do not compare 2 of them as
   * instants — use `sortKey`. It never converts to a zone. `null` and an unreadable value give
   * `null`.
   */
  static read(value) {
    const parsed = parse(value);
    if (parsed == null) {
      return null;
    }
    // `parse` builds a new moment for each call, so the caller can change it.
    return parsed.digits.utcOffset(parsed.offset, true);
  }

  /**
   * A number that orders values by their digits: the digits read as if they were UTC. It is not
   * an instant. `null` and an unreadable value give `null`.
   */
  static sortKey(value) {
    const parsed = parse(value);
    return parsed == null ? null : parsed.digits.valueOf();
  }

  /**
   * The digits of a value in `format`, as the customer booked them. The offset of the value and
   * the runtime zone do not move them. `null` and an unreadable value give the text of an
   * invalid moment (`Invalid date`), as `moment(value)` did. It never throws, so 1 bad value
   * does not stop an e-mail or a page.
   */
  static text(value, format) {
    const digits = WallClock.read(value);
    return digits == null ? moment.invalid().format(format) : digits.format(format);
  }

  /**
   * A compare function for `Array.prototype.sort` that orders 2 values by their digits. The
   * offsets do not count. A value with no readable digits sorts after a readable one. 2 values
   * with the same digits, or with no readable digits, compare as equal.
   */
  static compare(a, b) {
    const keyA = WallClock.sortKey(a);
    const keyB = WallClock.sortKey(b);
    if (keyA == null) {
      return keyB == null ? 0 : 1;
    }
    if (keyB == null) {
      return -1;
    }
    return keyA - keyB;
  }
}

module.exports = WallClock;
